import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';

import './style/BeFilterModal.css';

const formatBeLabel = value => {
	const text = String(value ?? '').trim();
	const match = text.match(/^(?:BE\s*)?0*(\d+)$/i);

	if (!match) return text;

	return `BE${String(Number(match[1])).padStart(2, '0')}`;
};

const PANEL_WIDTH = 360;
const PANEL_GAP = 12;

const BeFilterModal = ({
	open,
	productName,
	options,
	selectedBes = [],
	loading,
	updating,
	onSelectionChange,
	onClose,
}) => {
	const panelRef = useRef(null);
	const [position, setPosition] = useState({
		left: 390,
		bottom: 22,
	});

	useEffect(() => {
		if (!open || !panelRef.current) return;

		L.DomEvent.disableClickPropagation(panelRef.current);
		L.DomEvent.disableScrollPropagation(panelRef.current);
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;

		const updatesPopup = document.querySelector('.custom-popup-container');

		const updatePosition = () => {
			if (window.innerWidth <= 768) {
				setPosition({
					left: 12,
					bottom: 78,
				});
				return;
			}

			if (!updatesPopup) {
				setPosition({
					left: 390,
					bottom: 22,
				});
				return;
			}

			const rect = updatesPopup.getBoundingClientRect();
			const hasRoomOnRight =
				rect.right + PANEL_GAP + PANEL_WIDTH <= window.innerWidth - 12;

			if (hasRoomOnRight) {
				setPosition({
					left: rect.right + PANEL_GAP,
					bottom: Math.max(12, window.innerHeight - rect.bottom),
				});
				return;
			}

			// Fallback para telas menores: fica acima do popup de uploads.
			setPosition({
				left: Math.max(12, rect.left),
				bottom: Math.max(12, window.innerHeight - rect.top + PANEL_GAP),
			});
		};

		updatePosition();
		window.addEventListener('resize', updatePosition);
		updatesPopup?.addEventListener('transitionend', updatePosition);

		let resizeObserver;
		if (updatesPopup && typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(updatePosition);
			resizeObserver.observe(updatesPopup);
		}

		let mutationObserver;
		if (updatesPopup && typeof MutationObserver !== 'undefined') {
			mutationObserver = new MutationObserver(updatePosition);
			mutationObserver.observe(updatesPopup, {
				attributes: true,
				attributeFilter: ['style', 'class'],
			});
		}

		return () => {
			window.removeEventListener('resize', updatePosition);
			updatesPopup?.removeEventListener('transitionend', updatePosition);
			resizeObserver?.disconnect();
			mutationObserver?.disconnect();
		};
	}, [open]);

	if (!open) return null;

	const toggleBe = be => {
		const nextSelected = selectedBes.includes(be)
			? selectedBes.filter(item => item !== be)
			: [...selectedBes, be];

		onSelectionChange(nextSelected);
	};

	return (
		<div
			ref={panelRef}
			className="be-filter-panel"
			style={{
				left: position.left,
				bottom: position.bottom,
			}}
			role="region"
			aria-labelledby="be-filter-panel-title"
		>
			<div className="be-filter-panel-header">
				<div className="be-filter-panel-title-wrapper">
					<h3 id="be-filter-panel-title">Filtrar por BE</h3>
					<span>{productName}</span>
				</div>

				<button
					type="button"
					className="be-filter-panel-close"
					onClick={onClose}
					aria-label="Fechar filtro por BE"
				>
					×
				</button>
			</div>

			<div className="be-filter-panel-status">
				<span>
					{selectedBes.length === 0
						? 'Nenhum BE selecionado'
						: `${selectedBes.length} BE${
								selectedBes.length > 1 ? 's' : ''
						  } selecionado${selectedBes.length > 1 ? 's' : ''}`}
				</span>

				{updating && <span className="be-filter-updating">Atualizando mapa...</span>}
			</div>

			<div className="be-filter-panel-content">
				{loading ? (
					<p className="be-filter-message">Carregando BEs...</p>
				) : options.length === 0 ? (
					<p className="be-filter-message">
						Nenhum BE encontrado para este produto.
					</p>
				) : (
					<div className="be-options">
						{options.map(be => (
							<label key={String(be)} className="be-option">
								<span>{formatBeLabel(be)}</span>

								<input
									type="checkbox"
									checked={selectedBes.includes(be)}
									onChange={() => toggleBe(be)}
								/>
							</label>
						))}
					</div>
				)}
			</div>

			{selectedBes.length > 0 && (
				<div className="be-filter-panel-footer">
					<button
						type="button"
						className="be-filter-clear"
						onClick={() => onSelectionChange([])}
					>
						Limpar seleção
					</button>
				</div>
			)}
		</div>
	);
};

export default BeFilterModal;
